import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import type {
  FloorPlan,
  LiveDashboard,
  LiveOverview,
  PrepStatus,
  StationQueue,
  StationTicket,
} from './types'

/**
 * Tableau de bord du soir J.
 *
 * Un seul appel : le réseau du restaurant porte trois cents téléphones ce
 * soir-là, et la régie doit se peindre même quand il tousse. La cadence vient
 * du serveur (`refresh_seconds`, réglable par édition).
 */
export function useLiveDashboard(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['live-dashboard', eventId],
    queryFn: async () =>
      apiClient.get<LiveDashboard>('/ops/live', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: (query) => (query.state.data?.refresh_seconds ?? 15) * 1000,
  })
}

export function useStationQueue(
  station: 'kitchen' | 'bar',
  eventId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['station-queue', station, eventId],
    queryFn: async () =>
      apiClient.get<StationQueue>(`/ops/${station}/queue`, {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    // Une cuisine ne rafraîchit pas sa page : l'écran se tient à jour seul.
    refetchInterval: 10_000,
  })
}

export function useAdvancePrep(station: 'kitchen' | 'bar', eventId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      itemId,
      status,
      note,
    }: {
      itemId: string
      status: PrepStatus
      note?: string
    }) =>
      apiClient.post<StationTicket>(
        `/ops/${station}/items/${itemId}/status`,
        { prep_status: status, note: note ?? null },
        { token: await accessToken(), ...(eventId ? { eventId } : {}) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['station-queue', station, eventId] })
      void queryClient.invalidateQueries({ queryKey: ['live-dashboard', eventId] })
    },
  })
}

export function useFloorPlan(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['floor-plan', eventId],
    queryFn: async () =>
      apiClient.get<FloorPlan>('/ops/tables/floor', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 30_000,
  })
}

/**
 * La régie du propriétaire : toute la soirée en un appel.
 *
 * La cadence vient du serveur (`refresh_seconds`). Onglet caché, TanStack
 * suspend l'intervalle (`refetchIntervalInBackground` reste faux) : un
 * téléphone en poche ne doit pas tirer la page toutes les quinze secondes sur
 * le réseau saturé de la salle — il rattrape au retour au premier plan.
 */
export function useLiveOverview(eventId: string | undefined) {
  return useQuery({
    queryKey: ['live-overview', eventId],
    queryFn: async () =>
      apiClient.get<LiveOverview>('/ops/live/overview', {
        token: await accessToken(),
        ...(eventId ? { eventId } : {}),
      }),
    enabled: Boolean(eventId),
    refetchInterval: (query) => (query.state.data?.refresh_seconds ?? 15) * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}
